// bluaIQ — P2: Project Chat Module
// AI chat interface for project questions using local resolver + cached client
// Integrates VoiceInput component for speech-to-text

import { apiFetch } from '../utils/api'
import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { MessageCircle, Send, Bot, User, Trash2 } from 'lucide-react'
import { isDemoProject } from '../utils/demoData'
import VoiceInput from './VoiceInput'
import SavedProjectOnlyNotice from './SavedProjectOnlyNotice'

// ── TYPES ─────────────────────────────────────────

interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: string
}

// ── DEMO DATA ─────────────────────────────────────

const DEMO_MESSAGES: ChatMessage[] = [
  {
    id: 'msg-1', role: 'user', content: 'What\'s the total budget for this project?',
    timestamp: '2026-03-28T10:00:00',
  },
  {
    id: 'msg-2', role: 'assistant', content: 'The total budget for 4821 Elm Creek is $285,000. Here\'s the breakdown by major category:\n\n• Land/Lot: $65,000\n• Foundation: $18,500\n• Framing: $42,000\n• Roofing: $12,800\n• MEP: $38,000\n• Finishes: $45,000\n• Other: $63,700\n\nYou currently have 2 approved change orders totaling $6,000, bringing the adjusted budget to $291,000.',
    timestamp: '2026-03-28T10:00:05',
  },
  {
    id: 'msg-3', role: 'user', content: '¿Cuántos días de retraso tenemos?',
    timestamp: '2026-03-28T10:02:00',
  },
  {
    id: 'msg-4', role: 'assistant', content: 'Según las órdenes de cambio aprobadas, el impacto acumulado al cronograma es de 4 días:\n\n• CO #1 (Cubiertas de cuarzo): +3 días\n• CO #2 (Iluminación empotrada): +1 día\n\nAdemás hay una orden pendiente (CO #3 — Remediación de suelo) que agregaría 5 días más si se aprueba. El proyecto sigue dentro del margen de contingencia del cronograma.',
    timestamp: '2026-03-28T10:02:05',
  },
]

const QUICK_REPLY_SUGGESTIONS = [
  { en: 'What\'s the budget status?', es: '¿Cuál es el estado del presupuesto?' },
  { en: 'Any overdue tasks?', es: '¿Hay tareas atrasadas?' },
  { en: 'Show pending payments', es: 'Mostrar pagos pendientes' },
  { en: 'Who is the plumbing contact?', es: '¿Quién es el contacto de plomería?' },
]

// ── COMPONENT ─────────────────────────────────────

export default function ProjectChatModule() {
  const { getToken } = useAuth()
  const { projectId } = useParams()
  const { i18n } = useTranslation()
  const queryClient = useQueryClient()
  const isEs = i18n.language === 'es'
  const [input, setInput] = useState('')
  // Remember the most recent attempted message so the "Try again"
  // affordance after a failed send can resubmit without making the
  // user retype. Cleared on a successful send (`onSuccess` below).
  const [lastAttempt, setLastAttempt] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Real projects hit the backend chat route. The aiAdapter migration
  // (PR #133) lives entirely behind that route — this component never
  // calls AI directly. We destructure `isLoading` / `isError` /
  // `refetch` (where the original code only kept `data`) so the UI
  // can render honest loading and error states instead of a silent
  // empty screen when the API is slow or down.
  const { data: apiMessages, isLoading: isLoadingMessages, isError: isErrorLoading, refetch: refetchMessages } = useQuery<ChatMessage[]>({
    queryKey: ['project-chat', projectId],
    queryFn: async () => { const token = await getToken(); return apiFetch(`/api/projects/${projectId}/chat`, { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => {
      if (!r.ok) throw new Error('API unavailable')
      return r.json()
    }) },
    retry: false,
  })

  const isDemo = isDemoProject(projectId)
  const messages = Array.isArray(apiMessages) ? apiMessages : (isDemo ? DEMO_MESSAGES : [])

  const sendMutation = useMutation({
    mutationFn: async (content: string) => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/chat`, {
        method: 'POST',
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: content,
          context: { projectId, lang: i18n.language },
        }),
      })
      // Surface server failures as mutation errors instead of letting
      // a 500 response silently look like a successful send. Without
      // this, `sendMutation.isError` would never fire and the user
      // would just see their input cleared with no feedback.
      if (!res.ok) {
        throw new Error(`Send failed (${res.status})`)
      }
      return res.json()
    },
    onSuccess: () => {
      // Successful send → forget the retry buffer so a future error
      // banner isn't stale.
      setLastAttempt(null)
      queryClient.invalidateQueries({ queryKey: ['project-chat', projectId] })
    },
  })

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSend = () => {
    const text = input.trim()
    if (!text) return
    setInput('')
    setLastAttempt(text)
    sendMutation.mutate(text)
  }

  const handleVoiceTranscript = (text: string) => {
    setInput(text)
  }

  const handleQuickReply = (text: string) => {
    setInput('')
    setLastAttempt(text)
    sendMutation.mutate(text)
  }

  const handleRetrySend = () => {
    if (!lastAttempt) return
    sendMutation.reset()
    sendMutation.mutate(lastAttempt)
  }

  const formatTime = (ts: string) => {
    if (!ts) return ''
    const d = new Date(ts)
    if (isNaN(d.getTime())) return ''
    return d.toLocaleTimeString(isEs ? 'es-MX' : 'en-US', { hour: '2-digit', minute: '2-digit' })
  }

  return (
    <div className="p-5 max-w-3xl mx-auto flex flex-col" style={{ height: 'calc(100vh - 60px)' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17] flex items-center gap-2">
            <MessageCircle size={18} />
            {isEs ? 'Chat del Proyecto' : 'Project Chat'}
          </h2>
          <p className="text-[11px] font-[600] text-[#9E9890] mt-0.5">
            {isEs ? 'Pregunta lo que necesites sobre tu proyecto' : 'Ask anything about your project'}
          </p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-3 mb-4">
        {/* Loading state — only fires for REAL projects (demo projects
            skip the fetch via the demo fallback at `messages`). Without
            this branch the user sees a blank message list while the
            initial GET is in flight, which feels broken on slow
            connections. */}
        {!isDemo && isLoadingMessages && (
          <div className="text-center py-12" data-testid="chat-loading">
            <Bot size={36} className="mx-auto text-[#ECEAE4] mb-3" />
            <p className="text-[12px] font-[700] text-[#9E9890]">
              {isEs ? 'Cargando chat del proyecto…' : 'Loading project chat…'}
            </p>
          </div>
        )}

        {/* Load-error state — surfaces a failed GET so the user is not
            stuck staring at a silent empty list. Includes a bilingual
            "Try again" affordance that re-triggers the same query. */}
        {!isDemo && isErrorLoading && (
          <div className="text-center py-12" data-testid="chat-load-error">
            <Bot size={36} className="mx-auto text-[#9E9890] mb-3" />
            <p className="text-[12px] font-[700] text-[#1C1A17]">
              {isEs
                ? 'No pudimos cargar el chat del proyecto. Intenta de nuevo.'
                : 'We could not load Project Chat. Try again.'}
            </p>
            <button
              type="button"
              onClick={() => refetchMessages()}
              className="mt-3 inline-flex items-center gap-1 text-[11px] font-[800] text-[#06539B] hover:underline"
            >
              {isEs ? 'Intenta de nuevo' : 'Try again'}
            </button>
          </div>
        )}

        {/* Empty state — only when the load completed cleanly AND there
            are zero messages. The copy is intentionally a prompt to
            engage; no fake sample messages, no demo conversation. The
            AI-honesty disclaimer below the input is the durable note;
            this empty-state supporting line is the one-time framing. */}
        {!isDemo && !isLoadingMessages && !isErrorLoading && messages.length === 0 && (
          <div className="text-center py-12" data-testid="chat-empty">
            <Bot size={36} className="mx-auto text-[#ECEAE4] mb-3" />
            <p className="text-[14px] font-[800] text-[#1C1A17]">
              {isEs ? 'Haz una pregunta sobre este proyecto.' : 'Ask a question about this project.'}
            </p>
            <p className="text-[11px] font-[600] text-[#6B6660] mt-2 max-w-sm mx-auto leading-relaxed">
              {isEs
                ? 'Project Chat puede ayudarte a resumir información del proyecto, pero no reemplaza la revisión profesional.'
                : 'Project Chat can help summarize project information, but it does not replace professional review.'}
            </p>
          </div>
        )}

        {/* Demo-project empty state — preserved separately so the
            existing demo flow keeps its original lighter prompt. */}
        {isDemo && messages.length === 0 && (
          <div className="text-center py-12">
            <Bot size={36} className="mx-auto text-[#ECEAE4] mb-3" />
            <p className="text-[12px] font-[700] text-[#9E9890]">
              {isEs ? 'Empieza una conversación sobre tu proyecto' : 'Start a conversation about your project'}
            </p>
          </div>
        )}

        {messages.map(msg => (
          <div key={msg.id} className={`flex gap-2.5 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {msg.role === 'assistant' && (
              <div className="w-7 h-7 rounded-full bg-[#06539B] flex items-center justify-center flex-shrink-0 mt-0.5">
                <Bot size={14} className="text-white" />
              </div>
            )}
            <div className={`max-w-[80%] rounded-xl px-3.5 py-2.5 ${
              msg.role === 'user'
                ? 'bg-[#06539B] text-white'
                : 'bg-white border border-[#ECEAE4] text-[#1C1A17]'
            }`}>
              <p className="text-[14px] font-[600] whitespace-pre-wrap leading-relaxed">{msg.content}</p>
              <p className={`text-[9px] font-[600] mt-1 ${msg.role === 'user' ? 'text-white/50' : 'text-[#9E9890]'}`}>
                {formatTime(msg.timestamp || (msg as any).createdAt)}
              </p>
            </div>
            {msg.role === 'user' && (
              <div className="w-7 h-7 rounded-full bg-[#F2F0EB] flex items-center justify-center flex-shrink-0 mt-0.5">
                <User size={14} className="text-[#1C1A17]" />
              </div>
            )}
          </div>
        ))}

        {sendMutation.isPending && (
          <div className="flex gap-2.5" data-testid="chat-sending">
            <div className="w-7 h-7 rounded-full bg-[#06539B] flex items-center justify-center flex-shrink-0">
              <Bot size={14} className="text-white" />
            </div>
            <div className="bg-white border border-[#ECEAE4] rounded-xl px-3.5 py-2.5 flex items-center gap-2">
              <div className="flex gap-1">
                <div className="w-2 h-2 rounded-full bg-[#9E9890] animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 rounded-full bg-[#9E9890] animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 rounded-full bg-[#9E9890] animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
              <span className="text-[10px] font-[700] text-[#9E9890]">
                {isEs ? 'Enviando…' : 'Sending…'}
              </span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Quick replies */}
      {messages.length <= 2 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {QUICK_REPLY_SUGGESTIONS.map((qr, i) => (
            <button
              key={i}
              onClick={() => handleQuickReply(isEs ? qr.es : qr.en)}
              className="text-[10px] font-[700] text-[#06539B] bg-[#EEF2FF] px-3 py-1.5 rounded-full hover:bg-[#06539B] hover:text-white transition-colors"
            >
              {isEs ? qr.es : qr.en}
            </button>
          ))}
        </div>
      )}

      {/* Send-error banner — surfaces a failed POST so the user is
          not left wondering whether their question went through.
          The bilingual "Try again" button re-submits the last
          attempted message (`lastAttempt`) without making the user
          retype it. */}
      {sendMutation.isError && lastAttempt && (
        <div
          className="mb-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center justify-between gap-3"
          data-testid="chat-send-error"
        >
          <p className="text-[11px] font-[700] text-amber-800">
            {isEs
              ? 'No pudimos enviar tu mensaje. Intenta de nuevo.'
              : 'We could not send your message. Try again.'}
          </p>
          <button
            type="button"
            onClick={handleRetrySend}
            className="text-[11px] font-[800] text-amber-900 hover:underline"
          >
            {isEs ? 'Intenta de nuevo' : 'Try again'}
          </button>
        </div>
      )}

      {/* Input area
          PR #138B: in demo, the AI chat send POST `/api/projects/:id/chat`
          would hit a non-existent project and burn AI tokens. Replace the
          composer with the shared saved-project notice; the demo messages
          above still render. */}
      {isDemo ? (
        <SavedProjectOnlyNotice
          testId="chat-composer-demo-notice"
          bodyEn="Chat sending is available in saved projects. Create or open a real project to ask the AI about your work."
          bodyEs="El envío de chat está disponible en proyectos guardados. Crea o abre un proyecto real para preguntarle a la IA sobre tu obra."
        />
      ) : (
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-3">
          <div className="flex items-center gap-2">
            <VoiceInput onTranscript={handleVoiceTranscript} />
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
              placeholder={isEs ? 'Escribe tu pregunta...' : 'Type your question...'}
              className="flex-1 text-[14px] font-[600] focus:outline-none placeholder:text-[#9E9890]"
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || sendMutation.isPending}
              className="w-[36px] h-[36px] rounded-full bg-[#06539B] text-white flex items-center justify-center disabled:opacity-30"
              aria-label={sendMutation.isPending ? (isEs ? 'Enviando…' : 'Sending…') : (isEs ? 'Enviar' : 'Send')}
            >
              <Send size={14} />
            </button>
          </div>
        </div>
      )}

      {/* AI honesty footer — durable bilingual note that AI output
          should not be treated as authoritative. Lives below the
          input so it stays visible during normal conversation flow.
          Deterministic copy, no AI involvement. */}
      <p
        className="text-[10px] font-[600] text-[#9E9890] mt-2 text-center"
        data-testid="chat-ai-note"
      >
        {isEs
          ? 'Las respuestas de IA pueden estar incompletas. Verifica decisiones importantes.'
          : 'AI responses may be incomplete. Verify important decisions.'}
      </p>
    </div>
  )
}
